<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@chatwoot/pico-search';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import {
  resolveVariablesInMessage,
  stripUnsupportedFormatting,
} from 'dashboard/helper/editorHelper';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import CaretAnchoredPicker from 'dashboard/components-next/preview-picker/CaretAnchoredPicker.vue';

const props = defineProps({
  caretPosition: { type: Object, default: null },
  searchKey: { type: String, default: '' },
  variables: { type: Object, default: () => ({}) },
  schema: { type: Object, default: null },
});

const emit = defineEmits(['replace', 'close', 'removeTrigger']);
const SNIPPET_LEAD = 24;
const HIGHLIGHT_CLASS = 'text-n-blue-text';
const store = useStore();
const { t } = useI18n();
const { getPlainText, formatMessage, highlightContent } = useMessageFormatter();
const cannedResponses = useMapGetter('getCannedResponses');
const uiFlags = useMapGetter('getUIFlags');
const searchQuery = ref(props.searchKey);
const searchTerm = computed(() => searchQuery.value.trim());
const highlightMatches = text =>
  searchTerm.value
    ? highlightContent(text, searchTerm.value, HIGHLIGHT_CLASS)
    : text;
const buildSnippet = text => {
  const term = searchTerm.value;
  if (!term) return text;
  const index = text.toLowerCase().indexOf(term.toLowerCase());
  return index <= SNIPPET_LEAD ? text : `…${text.slice(index - SNIPPET_LEAD)}`;
};
const resolveContent = message =>
  stripUnsupportedFormatting(
    resolveVariablesInMessage(message, props.variables),
    props.schema
  );
const records = computed(() =>
  cannedResponses.value.map(
    ({ id, short_code: shortCode, content, attachments = [] }) => {
      const resolved = resolveContent(content);
      return {
        id,
        content,
        attachments,
        resolved,
        shortCode,
        plainText: getPlainText(resolved).replace(/\s+/g, ' ').trim(),
      };
    }
  )
);
const filteredRecords = computed(() =>
  searchTerm.value
    ? picoSearch(records.value, searchTerm.value, [
        { name: 'shortCode', weight: 1 },
        'plainText',
      ])
    : records.value
);
const items = computed(() =>
  filteredRecords.value.map(record => ({
    ...record,
    label: `/${record.shortCode}`,
    title: highlightMatches(`/${record.shortCode}`),
    subtitle: highlightMatches(buildSnippet(record.plainText)),
  }))
);
const onSelect = item =>
  emit('replace', { content: item.content, attachments: item.attachments });

onMounted(() => store.dispatch('getCannedResponse'));
</script>

<template>
  <CaretAnchoredPicker
    v-model:search="searchQuery"
    :caret-position="caretPosition"
    :items="items"
    :search-placeholder="t('COMBOBOX.SEARCH_PLACEHOLDER')"
    :is-loading="uiFlags.fetchingList"
    :empty-label="
      searchTerm
        ? t('COMBOBOX.EMPTY_SEARCH_RESULTS', { searchTerm })
        : t('COMBOBOX.EMPTY_STATE')
    "
    @select="onSelect"
    @close="emit('close')"
    @remove-trigger="emit('removeTrigger')"
  >
    <template #preview="{ item }">
      <div
        v-dompurify-html="formatMessage(item?.resolved || '')"
        class="px-4 py-3 prose prose-bubble !max-w-none prose-a:text-n-brand"
      />
    </template>
  </CaretAnchoredPicker>
</template>
